import React from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { FormSectionCard } from "./FormSectionCard"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectItemText, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Clock } from "lucide-react"
import { useLanguage } from '@/contexts/language-context'
import { useFormSectionNavigation } from '@/hooks/use-form-section-navigation'
import { useCodeValues } from '@/hooks/use-code-values'
import { TimesheetAndExpenseFormData } from '@/lib/schemas'
import { TIMESHEET_APPROVAL_TYPE, EXPENSE_METHODOLOGY } from '@/lib/constants/work-order-codes'
import Approvers from './Approvers'
import type { WorkerProfileSummary } from '@/types/api/engagement'

export interface TimesheetAndExpenseSectionProps {
  form: UseFormReturn<TimesheetAndExpenseFormData>
  isCompleted: boolean
  isReadonly?: boolean
  selectedInternalCompany?: number
  onApproverAdd: (userProfileId: number) => void
  onApproverRemove: (index: number) => void
  onExpenseApproverAdd: (userProfileId: number) => void
  onExpenseApproverRemove: (index: number) => void
  hiddenSections: Record<string, boolean>
  /** Server-resolved fallback names so already-assigned approvers render for a restricted caller. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

export const TimesheetAndExpenseSection = ({
  form,
  isCompleted,
  isReadonly = false,
  selectedInternalCompany = 0,
  onApproverAdd,
  onApproverRemove,
  onExpenseApproverAdd,
  onExpenseApproverRemove,
  hiddenSections,
  profileSummaries,
}: TimesheetAndExpenseSectionProps) => {
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('timesheet', hiddenSections)
  const { getCodeValues: getByCategory } = useCodeValues()

  // Watch approvers for conditional approval sequence display
  const approvers = useWatch({ control: form.control, name: 'approvers' }) || []
  
  // Auto-hide approval sequence when only 1 approver
  const showApprovalSequence = approvers.length > 1

  // Watch expense methodology for conditional expense approvers display
  const expenseMethodologyCode = useWatch({ control: form.control, name: 'expenseMethodologyCode' })
  const showExpenseApprovers = expenseMethodologyCode === EXPENSE_METHODOLOGY.ONLINE_APPROVAL

  return (
    <FormSectionCard
      id="timesheet"
      title={dictionary?.engagement?.timesheet?.title || "Timesheet and Expenses"}
      description={dictionary?.engagement?.timesheet?.description || "Manage timesheet and expense settings"}
      icon={Clock}
      isCompleted={isCompleted}
      showPrevNext={true}
      {...navigationProps}
    >
      <Form {...form}>
        {/* Timesheet Cycle */}
        <FormField
          control={form.control}
          name="selectedTimesheetCycle"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.timesheet?.timesheetCycle || "Timesheet Cycle"}
              </FormLabel>
              <FormControl>
                <Select
                  {...field}
                  onValueChange={field.onChange}
                  disabled={isReadonly}
                >
                  <SelectTrigger className="form-field-focus">
                    <SelectValue
                      placeholder={
                        dictionary?.engagement?.timesheet?.selectTimesheetCycle || "Select timesheet cycle"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getByCategory('workorder.CodeTimeSheetCycle').map(cycle => (
                      <SelectItem key={cycle.code} value={cycle.code!}>
                        {cycle.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Timesheet Approvers */}
        <Approvers
          form={form}
          isReadonly={isReadonly}
          selectedInternalCompany={selectedInternalCompany}
          onApproverAdd={onApproverAdd}
          onApproverRemove={onApproverRemove}
          profileSummaries={profileSummaries}
        />

        {/* Approval Sequence - only show when multiple approvers */}
        {showApprovalSequence && (
          <FormField
            control={form.control}
            name="selectedApprovalSequence"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.timesheet?.approvalSequence || "Approval Sequence"}
                </FormLabel>
                <FormControl>
                  <Select
                    {...field}
                    onValueChange={field.onChange}
                    disabled={isReadonly}
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue
                        placeholder={dictionary?.engagement?.timesheet?.selectApprovalSequence || "Select approval sequence"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getByCategory('workorder.CodeTimeSheetApprovalFlow').map(seq => (
                        <SelectItem key={seq.code} value={seq.code!} textValue={seq.text}>
                          <div className="space-y-1">
                            <SelectItemText className="font-medium">{seq.text}</SelectItemText>
                            <div className="text-xs text-muted-foreground">
                              {seq.code === TIMESHEET_APPROVAL_TYPE.SEQUENTIAL && (dictionary?.engagement?.timesheet?.sequentialHelp || "Each approver must approve before the next")}
                              {seq.code === TIMESHEET_APPROVAL_TYPE.PARALLEL && (dictionary?.engagement?.timesheet?.parallelHelp || "All approvers receive requests simultaneously")}
                              {seq.code === TIMESHEET_APPROVAL_TYPE.ALTERNATE && (dictionary?.engagement?.timesheet?.alternateHelp || "Only one approver needs to approve")}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Are Expenses Enabled 
            Note: For engagements, expense methodology is simplified to a Yes/No choice:
            - "Yes" maps to OnlineApproval (the only supported methodology for engagements)
            - "No" maps to NoExpense (expenses disabled)
        */}
        <FormField
          control={form.control}
          name="expenseMethodologyCode"
          render={({ field }) => {
            // Provide a deterministic default value when undefined
            const selectedValue = field.value || EXPENSE_METHODOLOGY.NO_EXPENSE
            
            return (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.expense?.areExpensesEnabled || "Are expenses enabled?"}
                </FormLabel>
                <FormControl>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={field.name}
                        checked={selectedValue === EXPENSE_METHODOLOGY.ONLINE_APPROVAL}
                        onChange={() => field.onChange(EXPENSE_METHODOLOGY.ONLINE_APPROVAL)}
                        disabled={isReadonly}
                        className="form-radio h-4 w-4"
                      />
                      <span className="text-sm">{dictionary?.common?.yes || "Yes"}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={field.name}
                        checked={selectedValue === EXPENSE_METHODOLOGY.NO_EXPENSE}
                        onChange={() => field.onChange(EXPENSE_METHODOLOGY.NO_EXPENSE)}
                        disabled={isReadonly}
                        className="form-radio h-4 w-4"
                      />
                      <span className="text-sm">{dictionary?.common?.no || "No"}</span>
                    </label>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )
          }}
        />

        {/* Expense Approver - only show when Online Approval is selected
            Note: currently only one internal approver is used, but the array shape is intentional for future multi-approver support.
        */}
        {showExpenseApprovers && (
          <Approvers
            form={form}
            isReadonly={isReadonly}
            selectedInternalCompany={selectedInternalCompany}
            onApproverAdd={onExpenseApproverAdd}
            onApproverRemove={onExpenseApproverRemove}
            fieldName="expenseApprovers"
            label={dictionary?.engagement?.expense?.internalApprover || "Internal Approver"}
            profileSummaries={profileSummaries}
          />
        )}
      </Form>
    </FormSectionCard>
  )
}

export default TimesheetAndExpenseSection

